import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productsApi, aggregatesApi, storesApi, categoriesApi, type AggregateSummary, purchasesApi, type Category, type LastKnownPrices, type ProductRatings } from '../lib/domain';
import { useScope, useAuth } from '../lib/hooks';
import { useUnits } from '../lib/units';
import { ApiError } from '../lib/api';
import { ImageThumbs } from '../components/ImageGallery';
import { BarcodeScanner } from '../components/BarcodeScanner';
import { ExternalDataMerge } from '../components/ExternalDataMerge';
import { PriceHistoryChart, type PricePoint } from '../components/PriceHistoryChart';

type ProductDetail = Awaited<ReturnType<typeof productsApi.get>>['product'];

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [scope] = useScope();
  const units = useUnits(scope);
  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [aggregates, setAggregates] = useState<AggregateSummary[]>([]);
  const [ean, setEan] = useState('');
  const [eanError, setEanError] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [brand, setBrand] = useState('');
  const [netQuantityValue, setNetQuantityValue] = useState('');
  const [netQuantityUnit, setNetQuantityUnit] = useState('');
  const [notes, setNotes] = useState('');
  const [shoppingNote, setShoppingNote] = useState('');
  const [preparation, setPreparation] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [blockNote, setBlockNote] = useState('');
  const [defaultStoreChainId, setDefaultStoreChainId] = useState('');
  const [chains, setChains] = useState<Array<{ id: string; name: string }>>([]);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pendingImage, setPendingImage] = useState<File | null>(null);
  const [pendingPreview, setPendingPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [prices, setPrices] = useState<LastKnownPrices | null>(null);
  const [pricesError, setPricesError] = useState<string | null>(null);
  const [pricePoints, setPricePoints] = useState<PricePoint[]>([]);
  const [ratings, setRatings] = useState<ProductRatings | null>(null);
  const [ratingError, setRatingError] = useState<string | null>(null);
  const [commentDraft, setCommentDraft] = useState('');
  const [commentLoaded, setCommentLoaded] = useState(false);
  const currentUserId = useAuth().user?.id;
  const myRating = ratings?.ratings.find((r) => r.userId === currentUserId)?.stars ?? 0;

  const load = useCallback(async () => {
    if (!id) return;
    const res = await productsApi.get(id);
    setProduct(res.product);
    setName(res.product.name);
    setBrand(res.product.brand ?? '');
    setNetQuantityValue(res.product.netQuantityValue ?? '');
    setNetQuantityUnit(res.product.netQuantityUnit ?? '');
    setNotes(res.product.notes ?? '');
    setShoppingNote(res.product.shoppingNote ?? '');
    setPreparation(res.product.preparation ?? '');
    setBlocked(!!res.product.blocked);
    setBlockNote(res.product.blockNote ?? '');
    setDefaultStoreChainId(res.product.defaultStoreChainId ?? '');
  }, [id]);

  useEffect(() => {
    storesApi.chains().then((r) => setChains(r.chains)).catch(() => undefined);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (scope) categoriesApi.list(scope).then((r) => setCategories(r.categories));
  }, [scope]);

  useEffect(() => {
    if (scope) aggregatesApi.list(scope).then((r) => setAggregates(r.aggregates)).catch(() => setAggregates([]));
  }, [scope]);

  // Nutzeranfrage: von der Produktseite aus soll man zu den (eigenen)
  // Preisen des Produkts kommen - unabhängig von der Community-Freigabe
  // im Preisvergleich (ComparePage), die eigene Kauf-/Beobachtungspreise
  // nicht ersetzt.
  useEffect(() => {
    if (!scope || !id) return;
    setPricesError(null);
    purchasesApi
      .lastKnown(scope, id)
      .then(setPrices)
      .catch((err) => setPricesError(err instanceof ApiError ? err.message : 'Preise konnten nicht geladen werden.'));
  }, [scope, id]);

  // Preisverlauf: Beobachtungen + Einkäufe mit Einzelpreis, je Geschäft.
  useEffect(() => {
    if (!scope || !id) return;
    Promise.all([purchasesApi.observationsFor(scope, id), purchasesApi.list(scope)])
      .then(([obs, pur]) => {
        const label = (b: { name: string; chain?: { name: string } | null } | null) =>
          b ? `${b.chain?.name ?? '?'} - ${b.name}` : 'Unbekanntes Geschäft';
        const pts: PricePoint[] = [];
        for (const o of obs.observations) {
          pts.push({ date: new Date(o.observedAt).getTime(), price: Number(o.price), storeKey: o.storeBranch?.id ?? '?', storeLabel: label(o.storeBranch) });
        }
        for (const p of pur.purchases) {
          for (const it of p.items) {
            if (it.productId !== id || it.unitPrice == null) continue;
            pts.push({ date: new Date(p.purchasedAt).getTime(), price: Number(it.unitPrice), storeKey: p.storeBranch?.id ?? '?', storeLabel: label(p.storeBranch) });
          }
        }
        setPricePoints(pts.filter((p) => Number.isFinite(p.price) && Number.isFinite(p.date)));
      })
      .catch(() => setPricePoints([]));
  }, [scope, id]);

  useEffect(() => {
    if (!id) return;
    productsApi.ratings(id).then(setRatings).catch(() => setRatings(null));
  }, [id]);

  // Eigenen Kommentar einmalig in das Eingabefeld übernehmen.
  useEffect(() => {
    if (!ratings || commentLoaded) return;
    setCommentDraft(ratings.ratings.find((r) => r.userId === currentUserId)?.comment ?? '');
    setCommentLoaded(true);
  }, [ratings, commentLoaded, currentUserId]);

  async function rate(stars: number) {
    if (!id) return;
    setRatingError(null);
    try {
      setRatings(await productsApi.rate(id, stars, commentDraft));
    } catch (err) {
      setRatingError(err instanceof ApiError ? err.message : 'Bewertung konnte nicht gespeichert werden.');
    }
  }

  async function removeRating() {
    if (!id) return;
    setRatingError(null);
    try {
      await productsApi.removeRating(id);
      setCommentDraft('');
      setRatings(await productsApi.ratings(id));
    } catch (err) {
      setRatingError(err instanceof ApiError ? err.message : 'Bewertung konnte nicht entfernt werden.');
    }
  }

  async function addBarcode() {
    if (!id || !ean.trim()) return;
    setEanError(null);
    try {
      await productsApi.addBarcode(id, ean.trim());
      setEan('');
      await load();
    } catch (err) {
      setEanError(err instanceof ApiError ? err.message : 'EAN konnte nicht hinzugefügt werden.');
    }
  }

  async function removeBarcode(barcodeId: string, code: string) {
    if (!id) return;
    if (!window.confirm(`EAN ${code} wirklich löschen?`)) return;
    setEanError(null);
    try {
      await productsApi.removeBarcode(id, barcodeId);
      await load();
    } catch (err) {
      setEanError(err instanceof ApiError ? err.message : 'EAN konnte nicht gelöscht werden.');
    }
  }

  async function saveDetails() {
    if (!id) return;
    if (!name.trim()) {
      setSaveState('error');
      setSaveError('Name darf nicht leer sein.');
      return;
    }
    setSaveState('saving');
    setSaveError(null);
    try {
      await productsApi.update(id, {
        name: name.trim(),
        brand: brand || null,
        netQuantityValue: netQuantityValue ? Number(netQuantityValue) : null,
        netQuantityUnit: netQuantityUnit || null,
        notes: notes.trim() || null,
        shoppingNote: shoppingNote.trim() || null,
        preparation: preparation.trim() || null,
        blocked,
        blockNote: blocked ? blockNote.trim() || null : null,
        defaultStoreChainId: defaultStoreChainId || null,
      });
      await load();
      setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 2500);
    } catch (err) {
      setSaveState('error');
      setSaveError(err instanceof ApiError ? err.message : 'Speichern fehlgeschlagen.');
    }
  }

  async function setCategory(categoryId: string) {
    if (!id) return;
    setSaveError(null);
    try {
      await productsApi.update(id, { categoryId: categoryId || null });
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Kategorie konnte nicht gespeichert werden.');
    }
  }

  async function setAggregate(aggregateId: string) {
    if (!id) return;
    setSaveError(null);
    try {
      await productsApi.assignAggregate(id, aggregateId || null);
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Sammelprodukt konnte nicht gespeichert werden.');
    }
  }

  useEffect(() => {
    if (!pendingImage) {
      setPendingPreview(null);
      return;
    }
    const url = URL.createObjectURL(pendingImage);
    setPendingPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [pendingImage]);

  async function uploadImage() {
    if (!id || !pendingImage) return;
    setSaveError(null);
    setUploading(true);
    try {
      await productsApi.uploadImage(id, pendingImage);
      setPendingImage(null);
      await load();
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Bild konnte nicht hochgeladen werden.');
    } finally {
      setUploading(false);
    }
  }

  if (!product) return <p>Lade…</p>;

  return (
    <div className="stack">
      <h1>{product.name}</h1>
      {product.archived && <span className="badge warn">Archiviert</span>}

      <div className="card stack">
        <h2>Details ergänzen (PRD-001)</h2>
        <div className="field">
          <label htmlFor="name">Name</label>
          <input id="name" value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="brand">Marke</label>
          <input id="brand" value={brand} onChange={(e) => setBrand(e.target.value)} />
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor="qty">Menge</label>
            <input id="qty" type="number" step="any" value={netQuantityValue} onChange={(e) => setNetQuantityValue(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="qty-unit">Einheit</label>
            <select id="qty-unit" value={netQuantityUnit} onChange={(e) => setNetQuantityUnit(e.target.value)}>
              <option value="">–</option>
              {units.units.map((u) => (
                <option key={u.code} value={u.code}>
                  {u.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="field">
          <label htmlFor="notes">Allgemeine Notiz</label>
          <textarea id="notes" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="default-store">Standardgeschäft</label>
          <select id="default-store" value={defaultStoreChainId} onChange={(e) => setDefaultStoreChainId(e.target.value)}>
            <option value="">Keins</option>
            {chains.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            Geschäft, in dem das Produkt in der Regel gekauft wird. Damit werden Tagesprodukte automatisch auf die passende Einkaufsliste verteilt.
          </p>
        </div>
        <div className="field">
          <label htmlFor="shopping-note">Einkaufsnotiz</label>
          <textarea id="shopping-note" rows={3} maxLength={2000} value={shoppingNote} onChange={(e) => setShoppingNote(e.target.value)} />
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            Wird im Einkaufsmodus als Hinweis angezeigt, z. B. „nur die Sorte mit blauer Packung“.
          </p>
        </div>
        <div className="field">
          <label>
            <input type="checkbox" checked={blocked} onChange={(e) => setBlocked(e.target.checked)} /> Auf Sperrliste setzen
          </label>
          {blocked && (
            <textarea aria-label="Notiz zur Sperrung" rows={2} maxLength={2000} placeholder="Notiz, z. B. Grund der Sperrung" value={blockNote} onChange={(e) => setBlockNote(e.target.value)} />
          )}
          <p className="muted" style={{ fontSize: '0.8rem' }}>
            Gesperrte Produkte lassen sich nicht auf Einkaufs- oder Merkliste setzen; die Notiz wird dabei angezeigt.
          </p>
        </div>
        <div className="field">
          <label htmlFor="preparation">Zubereitung</label>
          <textarea id="preparation" rows={5} maxLength={2000} value={preparation} onChange={(e) => setPreparation(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="category">Kategorie</label>
          <select id="category" value={product.categoryId ?? ''} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Unkategorisiert</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="aggregate">Sammelprodukt</label>
          <select id="aggregate" value={product.aggregateId ?? ''} onChange={(e) => setAggregate(e.target.value)}>
            <option value="">Keinem Sammelprodukt zugeordnet</option>
            {aggregates.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          {product.aggregateId && <Link to={`/aggregates/${product.aggregateId}`}>Sammelprodukt öffnen</Link>}
        </div>
        <div className="row">
          <button className="primary" onClick={saveDetails} disabled={saveState === 'saving'}>
            {saveState === 'saving' ? 'Speichert…' : 'Speichern'}
          </button>
          {saveState === 'saved' && <span className="badge ok" role="status">Gespeichert</span>}
        </div>
        {saveError && (
          <p className="error-text" role="alert">
            {saveError}
          </p>
        )}
      </div>

      <div className="card stack">
        <h2>EAN / Barcodes</h2>
        <ul className="list-reset">
          {product.barcodes.map((b) => (
            <li key={b.id} className="row">
              <span>{b.ean}</span>
              <button
                type="button"
                className="secondary"
                aria-label={`EAN ${b.ean} löschen`}
                onClick={() => removeBarcode(b.id, b.ean)}
              >
                Löschen
              </button>
            </li>
          ))}
        </ul>
        <div className="row">
          <input value={ean} onChange={(e) => setEan(e.target.value)} placeholder="Neue EAN" />
          <button onClick={addBarcode}>Hinzufügen</button>
        </div>
        <BarcodeScanner label="📷 EAN scannen" onDetect={(code) => setEan(code)} onError={setEanError} />
        {eanError && <p className="error-text">{eanError}</p>}
      </div>

      {product.barcodes.length > 0 && (
        <div className="card stack">
          <h2>Daten von OpenFoodFacts</h2>
          <ExternalDataMerge productId={product.id} current={product} units={units.units} onApplied={load} />
        </div>
      )}

      <div className="card stack" id="preise">
        <h2>Preise</h2>
        {pricesError && <p className="error-text">{pricesError}</p>}
        {prices?.lastPurchase && (
          <div>
            <strong>Zuletzt gekauft:</strong> {Number(prices.lastPurchase.unitPrice ?? 0).toFixed(2)} € am{' '}
            {new Date(prices.lastPurchase.purchase.purchasedAt).toLocaleDateString('de-DE')}
            {prices.lastPurchase.purchase.storeBranch && ` bei ${prices.lastPurchase.purchase.storeBranch.chain?.name ?? '?'} - ${prices.lastPurchase.purchase.storeBranch.name}`}
          </div>
        )}
        {prices?.lastObservation && (
          <div>
            <strong>Zuletzt beobachtet:</strong> {Number(prices.lastObservation.price).toFixed(2)} € am{' '}
            {new Date(prices.lastObservation.observedAt).toLocaleDateString('de-DE')}
            {prices.lastObservation.storeBranch && ` bei ${prices.lastObservation.storeBranch.chain?.name ?? '?'} - ${prices.lastObservation.storeBranch.name}`}
          </div>
        )}
        {pricePoints.length > 0 && <PriceHistoryChart points={pricePoints} />}
        {prices && !prices.lastPurchase && !prices.lastObservation && <p className="muted">Noch keine eigenen Preisdaten für dieses Produkt.</p>}
        {product.barcodes.length > 0 ? (
          <Link to={`/compare?ean=${encodeURIComponent(product.barcodes[0].ean)}`}>Community-Preisvergleich öffnen</Link>
        ) : (
          <p className="muted">Community-Preisvergleich benötigt eine EAN (siehe oben).</p>
        )}
      </div>

      <div className="card stack" id="bewertungen">
        <h2>Bewertungen</h2>
        {ratings && ratings.count > 0 && ratings.average != null && (
          <div>
            <strong>Ø {ratings.average.toFixed(1)} von 5</strong> ({ratings.count} {ratings.count === 1 ? 'Bewertung' : 'Bewertungen'})
          </div>
        )}
        <div className="row" role="group" aria-label="Eigene Bewertung">
          {[1, 2, 3, 4, 5].map((n) => {
            const mine = myRating;
            return (
              <button key={n} onClick={() => rate(n)} aria-label={`${n} ${n === 1 ? 'Stern' : 'Sterne'}`} aria-pressed={mine === n} style={{ fontSize: '1.4rem', color: n <= mine ? '#f5a623' : undefined }}>
                {n <= mine ? '★' : '☆'}
              </button>
            );
          })}
          {ratings?.ratings.some((r) => r.userId === currentUserId) && <button onClick={removeRating}>Bewertung entfernen</button>}
        </div>
        {myRating > 0 && (
          <div className="field">
            <label htmlFor="rating-comment">Kommentar zur Bewertung (optional)</label>
            <textarea id="rating-comment" rows={3} maxLength={1000} value={commentDraft} onChange={(e) => setCommentDraft(e.target.value)} />
            <button onClick={() => rate(myRating)}>Kommentar speichern</button>
          </div>
        )}
        {myRating === 0 && <p className="muted">Vergib zuerst Sterne, um einen Kommentar zu ergänzen.</p>}
        {ratingError && <p className="error-text" role="alert">{ratingError}</p>}
        {ratings && ratings.count === 0 && <p className="muted">Noch keine Bewertungen.</p>}
        <ul className="list-reset">
          {ratings?.ratings.map((r) => (
            <li key={r.id}>
              <span aria-label={`${r.stars} von 5 Sternen`} style={{ color: '#f5a623' }}>{'★'.repeat(r.stars)}{'☆'.repeat(5 - r.stars)}</span> {r.username} ·{' '}
              {new Date(r.updatedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })}
              {r.comment && <div style={{ whiteSpace: 'pre-wrap' }}>{r.comment}</div>}
            </li>
          ))}
        </ul>
      </div>

      <div className="card stack">
        <h2>Bilder</h2>
        <div className="row">
          <ImageThumbs images={product.images} alt={product.name} />
        </div>
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          aria-label="Bild auswählen oder fotografieren"
          onChange={(e) => {
            setPendingImage(e.target.files?.[0] ?? null);
            e.target.value = '';
          }}
        />
        {pendingImage && pendingPreview && (
          <div className="stack">
            <img src={pendingPreview} alt="Vorschau" style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }} />
            <div className="row">
              <button type="button" onClick={uploadImage} disabled={uploading}>
                {uploading ? 'Lade hoch…' : 'Bild speichern'}
              </button>
              <button type="button" className="secondary" onClick={() => setPendingImage(null)} disabled={uploading}>
                Abbrechen
              </button>
            </div>
          </div>
        )}
      </div>

      {!product.archived && (
        <button
          className="danger"
          onClick={async () => {
            if (!id) return;
            try {
              await productsApi.archive(id);
              await load();
            } catch (err) {
              setSaveError(err instanceof ApiError ? err.message : 'Archivieren fehlgeschlagen.');
            }
          }}
        >
          Produkt archivieren
        </button>
      )}
    </div>
  );
}
